import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface Package {
  id: string;
  name: string;
  price: number;
  description: string | null;
  features: string[];
  is_featured: boolean;
}

export function PackagesPreview() {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      const { data } = await supabase
        .from("pricing_packages")
        .select("id, name, price, description, features, is_featured")
        .eq("is_active", true)
        .eq("show_on_homepage", true);

      if (data) {
        // Sort by price descending, featured always 2nd
        const sorted = [...data].sort((a, b) => b.price - a.price);
        const featuredIdx = sorted.findIndex((p) => p.is_featured);
        if (featuredIdx !== -1 && featuredIdx !== 1 && sorted.length > 1) {
          const [featured] = sorted.splice(featuredIdx, 1);
          sorted.splice(1, 0, featured);
        }
        // Limit to 3 packages on homepage
        setPackages(sorted.slice(0, 3));
      }
      setLoading(false);
    };
    fetchPackages();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };


  if (loading) {
    return (
      <section className="section-padding bg-muted/30">
        <div className="container-wide">
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </div>
      </section>
    );
  }

  if (packages.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-muted/30">
      <div className="container-wide">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
            Simple, Transparent Pricing
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            No hidden fees. No pressure. Just beautiful wedding coverage at prices that make sense.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -8 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 300 }}
              className={`relative cursor-pointer rounded-2xl border p-8 transition-shadow duration-300 ${
                pkg.is_featured
                  ? "border-primary bg-card shadow-rose hover:shadow-xl hover:shadow-primary/20"
                  : "border-border bg-card hover:shadow-lg hover:shadow-foreground/5"
              }`}
            >
              {pkg.is_featured && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground shadow-rose">
                  Most Popular
                </span>
              )}
              <div className="text-center">
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  {pkg.name}
                </h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-foreground">{formatPrice(pkg.price)}</span>
                </div>
                
                <p className="mt-3 text-sm text-muted-foreground">{pkg.description}</p>
              </div>
              <ul className="mt-8 space-y-4">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={pkg.is_featured ? "hero" : "hero-outline"}
                size="lg"
                className="mt-8 w-full"
                asChild
              >
                <Link to={`/contact?package=${encodeURIComponent(pkg.name)}`}>Check Availability</Link>
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}